import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

export interface HttpErrorContext {
  retryAttempt?: number;
  maxRetries?: number;
  userAgent?: string;
  timestamp?: string;
}

export interface SentryHttpErrorConfig {
  ignoredStatuses: number[];
  ignoredUrlPatterns: string[];
  sensitiveHeaders: string[];
  sensitiveFields: string[];
}

@Injectable({
  providedIn: 'root',
})
export class SentryHttpErrorHandlerService {
  private readonly config: SentryHttpErrorConfig = {
    ignoredStatuses: [401, 403, 404, 417],
    ignoredUrlPatterns: ['/api/health', '/api/ping', '/me'],
    sensitiveHeaders: ['authorization', 'cookie', 'x-api-key', 'x-auth-token'],
    sensitiveFields: ['password', 'token', 'secret', 'key', 'creditCard', 'ssn'],
  };

  captureHttpError(error: HttpErrorResponse, request: HttpRequest<unknown>, context: HttpErrorContext = {}): void {
    if (this.shouldIgnoreError(error, request)) {
      return;
    }

    Sentry.withScope((scope) => {
      this.setHttpErrorTags(scope, error, request, context);
      this.setHttpErrorContexts(scope, error, request, context);
      this.captureErrorBasedOnStatus(error, request);
    });
  }

  addRetryBreadcrumb(request: HttpRequest<unknown>, attempt: number, maxAttempts: number): void {
    Sentry.addBreadcrumb({
      message: `Retrying network request (attempt ${attempt}/${maxAttempts})`,
      category: 'http.retry',
      data: {
        url: this.sanitizeUrl(request.url),
        method: request.method,
        attempt,
        maxAttempts,
      },
      level: 'info',
      timestamp: Date.now() / 1000,
    });
  }

  addErrorBreadcrumb(error: HttpErrorResponse, request: HttpRequest<unknown>): void {
    Sentry.addBreadcrumb({
      message: `HTTP Error: ${error.status} ${error.statusText}`,
      category: 'http.error',
      data: {
        url: this.sanitizeUrl(request.url),
        method: request.method,
        status: error.status,
        statusText: error.statusText,
      },
      level: 'error',
      timestamp: Date.now() / 1000,
    });
  }

  captureNetworkError(request: HttpRequest<unknown>): void {
    Sentry.withScope((scope) => {
      scope.setTag('error.category', 'network');
      scope.setLevel('error');
      scope.setContext('Network Error Details', {
        url: this.sanitizeUrl(request.url),
        method: request.method,
        timestamp: new Date().toISOString(),
      });
      Sentry.captureMessage('Network connectivity issue detected', 'error');
    });
  }

  captureBusinessLogicError(error: HttpErrorResponse, request: HttpRequest<unknown>): void {
    Sentry.withScope((scope) => {
      scope.setTag('error.category', 'business_logic');
      scope.setLevel('warning');
      scope.setContext('Business Logic Error', {
        url: this.sanitizeUrl(request.url),
        method: request.method,
        errorCode: error.error?.code,
        errorMessage: error.error?.message,
        timestamp: new Date().toISOString(),
      });
      Sentry.captureMessage('Business logic error - Expectation Failed', 'warning');
    });
  }

  captureValidationError(error: HttpErrorResponse, request: HttpRequest<unknown>): void {
    Sentry.withScope((scope) => {
      scope.setTag('error.category', 'validation');
      scope.setLevel('warning');
      scope.setContext('Validation Error', {
        url: this.sanitizeUrl(request.url),
        method: request.method,
        validationErrors: this.sanitizeResponseBody(error.error),
        timestamp: new Date().toISOString(),
      });
      Sentry.captureMessage('Input validation error occurred', 'warning');
    });
  }

  capturePermissionError(request: HttpRequest<unknown>): void {
    Sentry.withScope((scope) => {
      scope.setTag('error.category', 'permission');
      scope.setLevel('warning');
      scope.setContext('Permission Error', {
        url: this.sanitizeUrl(request.url),
        method: request.method,
        timestamp: new Date().toISOString(),
      });
      Sentry.captureMessage('Forbidden access attempt detected', 'warning');
    });
  }

  captureUnhandledError(error: HttpErrorResponse, request: HttpRequest<unknown>): void {
    Sentry.withScope((scope) => {
      scope.setTag('error.category', 'unhandled_http');
      scope.setLevel('error');
      scope.setContext('Unhandled HTTP Error', {
        status: error.status,
        statusText: error.statusText,
        url: this.sanitizeUrl(request.url),
        method: request.method,
        message: error.message,
        timestamp: new Date().toISOString(),
      });
      Sentry.captureException(error);
    });
  }

  addNavigationBreadcrumb(reason: string, originalUrl: string, targetRoute: string): void {
    Sentry.addBreadcrumb({
      message: `Navigation triggered: ${reason}`,
      category: 'navigation',
      data: {
        reason,
        originalUrl: this.sanitizeUrl(originalUrl),
        targetRoute,
        timestamp: new Date().toISOString(),
      },
      level: 'info',
      timestamp: Date.now() / 1000,
    });
  }

  private shouldIgnoreError(error: HttpErrorResponse, request: HttpRequest<unknown>): boolean {
    if (this.config.ignoredStatuses.includes(error.status)) {
      return true;
    }

    return this.config.ignoredUrlPatterns.some((pattern) => request.url.includes(pattern));
  }

  private setHttpErrorTags(
    scope: any,
    error: HttpErrorResponse,
    request: HttpRequest<unknown>,
    context: HttpErrorContext,
  ): void {
    scope.setTag('error.type', 'http');
    scope.setTag('http.method', request.method);
    scope.setTag('http.status_code', error.status);
    scope.setTag('http.url', this.sanitizeUrl(request.url));

    if (context.retryAttempt !== undefined) {
      scope.setTag('retry.attempt', context.retryAttempt);
      scope.setTag('retry.max_attempts', context.maxRetries || 3);
    }
  }

  private setHttpErrorContexts(
    scope: any,
    error: HttpErrorResponse,
    request: HttpRequest<unknown>,
    context: HttpErrorContext,
  ): void {
    scope.setContext('HTTP Request', {
      url: this.sanitizeUrl(request.url),
      method: request.method,
      headers: this.sanitizeHeaders(request.headers),
      body: this.sanitizeRequestBody(request.body),
      timestamp: context.timestamp || new Date().toISOString(),
    });

    scope.setContext('HTTP Response', {
      status: error.status,
      statusText: error.statusText,
      message: error.message,
      error: this.sanitizeResponseBody(error.error),
      timestamp: new Date().toISOString(),
    });
  }

  private captureErrorBasedOnStatus(error: HttpErrorResponse, request: HttpRequest<unknown>): void {
    if (error.status === 0) {
      Sentry.captureMessage(`Network error: ${request.method} ${this.sanitizeUrl(request.url)}`, 'error');
    } else {
      Sentry.captureException(error);
    }
  }

  private sanitizeUrl(url: string): string {
    try {
      const urlObj = new URL(url, window.location.origin);
      const sensitiveParams = ['token', 'key', 'password', 'secret'];

      sensitiveParams.forEach((param) => {
        if (urlObj.searchParams.has(param)) {
          urlObj.searchParams.set(param, '[REDACTED]');
        }
      });

      return urlObj.toString();
    } catch {
      return url;
    }
  }

  private sanitizeHeaders(headers: any): Record<string, string> {
    const sanitized: Record<string, string> = {};

    headers.keys()?.forEach((key: string) => {
      const lowerKey = key.toLowerCase();
      if (this.config.sensitiveHeaders.includes(lowerKey)) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = headers.get(key);
      }
    });

    return sanitized;
  }

  private sanitizeRequestBody(body: any): any {
    if (!body || typeof body !== 'object') {
      return body;
    }

    const sanitized = { ...body };

    this.config.sensitiveFields.forEach((field) => {
      if (sanitized[field]) {
        sanitized[field] = '[REDACTED]';
      }
    });

    return sanitized;
  }

  private sanitizeResponseBody(responseBody: any): any {
    if (!responseBody || typeof responseBody !== 'object') {
      return responseBody;
    }

    const sanitized = { ...responseBody };

    this.config.sensitiveFields.forEach((field) => {
      if (sanitized[field]) {
        sanitized[field] = '[REDACTED]';
      }
    });

    return sanitized;
  }
}
